import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { usePVData } from '../context/PVDataContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiSave, FiCalendar } = FiIcons;

const MonthlyDataModal = ({ plant, onClose }) => {
  const { updateMonthlyData, getMonthlyData } = usePVData();
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [monthlyValues, setMonthlyValues] = useState({});

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  const months = [
    'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
    'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
  ];

  React.useEffect(() => {
    const values = {};
    for (let month = 1; month <= 12; month++) {
      values[month] = getMonthlyData(plant.id, month, selectedYear);
    }
    setMonthlyValues(values);
  }, [selectedYear, plant.id, getMonthlyData]);

  const handleValueChange = (month, value) => {
    setMonthlyValues(prev => ({
      ...prev,
      [month]: parseFloat(value) || 0
    }));
  };

  const handleSave = () => {
    Object.entries(monthlyValues).forEach(([month, value]) => {
      updateMonthlyData(plant.id, parseInt(month), selectedYear, parseFloat(value) || 0);
    });
    onClose();
  };

  const totalYear = Object.values(monthlyValues).reduce((sum, value) => sum + (parseFloat(value) || 0), 0);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Monatsdaten eingeben</h2>
            <p className="text-gray-600">{plant.name}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <SafeIcon icon={FiX} className="text-2xl" />
          </button>
        </div>

        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <SafeIcon icon={FiCalendar} className="text-gray-500" />
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                {years.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            
            <div className="text-right">
              <p className="text-sm text-gray-600">Jahresertrag</p>
              <p className="text-2xl font-bold text-primary-600">{totalYear.toLocaleString()} kWh</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {months.map((monthName, index) => {
              const monthNumber = index + 1;
              return (
                <div key={monthNumber} className="bg-gray-50 p-4 rounded-lg">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {monthName}
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.1"
                      value={monthlyValues[monthNumber] || ''}
                      onChange={(e) => handleValueChange(monthNumber, e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="0"
                    />
                    <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">
                      kWh
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Abbrechen
            </button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleSave}
              className="bg-primary-500 text-white px-6 py-3 rounded-lg hover:bg-primary-600 transition-colors flex items-center space-x-2"
            >
              <SafeIcon icon={FiSave} />
              <span>Speichern</span>
            </motion.button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default MonthlyDataModal;